<template>
  <div>
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Entries</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
            <b-button
              variant="primary"
              :to="{ name: 'house-pin-create'}"
            >
              Add House Pin
            </b-button>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchKeyword"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="housePinData"
        :fields="tableColumns"
        responsive
        striped
        bordered
        hover
        show-empty
        empty-text="No matching records found"
        class="position-relative"
      >
        <template
          slot="top-row"
          slot-scope="{ fields }"
        >
          <td
            v-for="field in fields"
            :key="field.key"
          >
            <b-form-input
              v-if="field.key === 'id'"
              v-model="filterId"
              :placeholder="field.label"
            />

            <b-form-input
              v-if="field.key === 'name'"
              v-model="filterName"
              :placeholder="field.label"
            />

            <v-select
              v-if="field.key === 'zone_name'"
              v-model="filterZone"
              :options="zoneOptions"
              label="name"
              :reduce="item => item.id"
              clearable
            />

            <v-select
              v-if="field.key === 'bts_name'"
              v-model="filterBts"
              :options="btsOptions"
              label="name"
              :reduce="item => item.id"
              clearable
            />

            <v-select
              v-if="field.key === 'mrt_name'"
              v-model="filterMrt"
              :options="mrtOptions"
              label="name"
              :reduce="item => item.id"
              clearable
            />

            <v-select
              v-if="field.key === 'airport_name'"
              v-model="filterAirport"
              :options="airportOptions"
              label="name"
              :reduce="item => item.id"
              clearable
            />
          </td>
        </template>
        <template #cell(image)="data">
          <b-link
            variant="flat-primary"
            class="pb0"
            :to="{ name: 'house-pin-upload', params: { id: data.item.id } }"
          >
            <b-img
              v-if="data.item.image"
              :src="data.item.image"
              fluid
              alt="Property image"
              class="img_property"
              style="max-width: 120px;"
            />
            <b-img
              v-else
              class="img_property"
              :src="'https://digitalnation.sgp1.cdn.digitaloceanspaces.com/findbangkokroom/storage/1698858264911-picture.png'"
              fluid
            />
            <!-- ของเดิมมี v-else ซ้อนกันสามชั้น ตัวสุดท้ายไม่มีวันทำงาน (b-img v-else ครอบไว้แล้ว) — เอาออก -->
          </b-link>
          <b-link
            variant="flat-primary"
            class="pb0"
            :to="{ name: 'house-pin-floor-plan', params: { id: data.item.id } }"
          >
            Floor Plan
          </b-link>
          <!-- แผงเดียวกับหน้าหมุดคอนโด ต่างที่ owner-type ซึ่งบอก API ว่าอ่านตารางไหน -->
          <b-button
            class="ml-1"
            size="sm"
            variant="success"
            @click="popupNearBy(data.item)"
          >
            ใกล้เคียง
          </b-button>
        </template>

        <template #cell(name)="data">
          <router-link :to="{ name: 'house-pin-edit', params: { id: data.item.id } }">
            {{ data.item.name[currentLocale] || '' }}
          </router-link>
        </template>

        <template #cell(zone_name)="data">
          <div>{{ formatZoneName(data.item.zones) }}</div>
        </template>

        <template #cell(bts_name)="data">
          <div>{{ formatName(data.item.btsData) }}</div>
        </template>

        <template #cell(mrt_name)="data">
          <div>{{ formatName(data.item.mrtData) }}</div>
        </template>

        <template #cell(airport_name)="data">
          <div>{{ formatName(data.item.airportLinkData) }}</div>
        </template>

        <template #cell(is_active)="data">
          <b-form-checkbox
            :checked="data.item.is_active === 1"
            class="custom-control-primary"
            name="is_active"
            switch
          />
        </template>

        <template #cell(actions)="data">
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>

              <b-dropdown-item :to="{ name: 'house-pin-edit', params: { id: data.item.id } }">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteconfirm(data.item.id)">
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </template>
      </b-table>

      <div class="m-2 d-flex justify-content-between align-items-center">
        <div>
          <span>
            Showing {{ (currentPage - 1) * perPage + 1 }}
            –
            {{
              Math.min(currentPage * perPage, totalRows)
            }}
            of {{ totalRows }} entries
          </span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          size="md"
          class="mb-0"
        />
      </div>
    </b-card>

    <near-by-list
      :is-near-by-sidebar-active.sync="isNearBySidebarActive"
      :place-data="selectedPin"
      owner-type="house_pin"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BFormInput, BTable, BImg, BFormCheckbox, BDropdown, BDropdownItem,
  BPagination, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@axios'
import nearByList from '@/views/property-pins/pin-lists/nearByList.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    vSelect,
    BFormInput,
    BTable,
    BImg,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BPagination,
    BLink,
    nearByList,
  },
  data() {
    return {
      tableColumns: [
        {
          key: 'id', label: 'ID', sortable: true, tdClass: 'width-100',
        },
        {
          key: 'image', label: 'Image', sortable: false, tdClass: 'width-200 p-1',
        },
        { key: 'name', label: 'Name of House pin', sortable: true },
        { key: 'rate', label: 'Rate', sortable: false },
        {
          key: 'zone_name', label: 'Zone', sortable: false, tdClass: 'width-150',
        },
        {
          key: 'bts_name', label: 'BTS', sortable: false, tdClass: 'width-150',
        },
        {
          key: 'mrt_name', label: 'MRT', sortable: false, tdClass: 'width-150',
        },
        {
          key: 'airport_name', label: 'Airport Link', sortable: false, tdClass: 'width-150',
        },
        {
          key: 'is_active', label: 'Active', sortable: false, tdClass: 'width-100',
        },
        { key: 'actions', label: 'Actions', tdClass: 'width-100' },
      ],
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      searchKeyword: '',
      housePinData: [],
      currentPage: 1,
      totalRows: 0,
      filterId: null,
      filterName: null,
      filterZone: null,
      filterBts: null,
      filterMrt: null,
      filterAirport: null,
      zoneOptions: [],
      btsOptions: [],
      mrtOptions: [],
      airportOptions: [],
      /* แผง "ใกล้เคียง" — ใช้คอมโพเนนต์ตัวเดียวกับหน้าหมุดคอนโด */
      isNearBySidebarActive: false,
      selectedPin: {},
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
  },
  watch: {
    perPage: 'getHousePinLists',
    currentPage: 'getHousePinLists',
    searchKeyword: 'getHousePinLists',
    filterId: 'getHousePinLists',
    filterName: 'getHousePinLists',
    filterZone: 'getHousePinLists',
    filterBts: 'getHousePinLists',
    filterMrt: 'getHousePinLists',
    filterAirport: 'getHousePinLists',
  },
  created() {
    this.getHousePinLists()
    this.getZoneData()
    this.getBtsData()
    this.getMrtData()
    this.getAirportLinkData()
  },
  methods: {
    /* เปิดแผงสถานที่ใกล้เคียงของหมุดที่กด */
    popupNearBy(pin) {
      this.selectedPin = pin
      this.isNearBySidebarActive = true
    },
    async getHousePinLists() {
      try {
        const { data } = await axios.post('v1/house-pin/list', {
          perPage: this.perPage,
          currentPage: this.currentPage,
          keyword: this.searchKeyword,
          order_by: 'id ASC',

          id: this.filterId,
          name: this.filterName,
          zone_id: this.filterZone,
          bts_id: this.filterBts,
          mrt_id: this.filterMrt,
          airport_id: this.filterAirport,
        })

        this.housePinData = data.data
        this.totalRows = data.total || data.totalRows || 0
      } catch (err) {
        console.error('Error fetching house list:', err)
      }
    },
    async getZoneData() {
      try {
        const { data } = await axios.post('/zone/v1/zoneData', {})

        this.zoneOptions = data.data
      } catch (err) {
        console.error('Error fetching zone list:', err)
      }
    },
    async getBtsData() {
      try {
        const { data } = await axios.post('/transit/v1/filterByType', { type_code: 'bts' })

        this.btsOptions = data.data
      } catch (err) {
        console.error('Error fetching bts list:', err)
      }
    },
    async getMrtData() {
      try {
        const { data } = await axios.post('/transit/v1/filterByType', { type_code: 'mrt' })

        this.mrtOptions = data.data
      } catch (err) {
        console.error('Error fetching mrt list:', err)
      }
    },
    async getAirportLinkData() {
      try {
        const { data } = await axios.post('/transit/v1/filterByType', { type_code: 'airport' })

        this.airportOptions = data.data
      } catch (err) {
        console.error('Error fetching mrt list:', err)
      }
    },
    formatName(data) {
      const names = data ? data.map(item => item.name) : []
      return names.join(', ')
    },
    formatZoneName(data) {
      const names = data.map(zone => zone.name[this.currentLocale])
      return names.join(', ')
    },
  },
}
</script>
